import { ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, ArrowLeft } from "lucide-react";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Separator } from "@/components/ui/separator";
import { ROUTES } from "@/lib/routeConstants";

interface LegalPageLayoutProps {
  readonly title: string;
  readonly lastUpdated?: string;
  readonly children: ReactNode;
}

export function LegalPageLayout({
  title,
  lastUpdated,
  children,
}: LegalPageLayoutProps) {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <main className="flex min-h-[100dvh] flex-col bg-background">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-3 py-2.5 sm:px-8 sm:py-4 md:px-16">
          <div className="flex min-w-0 items-center justify-between gap-2 sm:gap-4">
            <Link
              to={ROUTES.LANDING}
              className="flex min-w-0 items-center gap-2"
            >
              <div className="shrink-0 rounded-lg bg-primary p-2 shadow-sm">
                <BookOpen className="h-5 w-5 text-primary-foreground sm:h-6 sm:w-6" />
              </div>
              <span className="font-ui truncate text-lg font-bold text-foreground sm:text-xl">
                DeutschGenie
              </span>
            </Link>

            <div className="flex items-center gap-2">
              <Link
                to={ROUTES.LANDING}
                className="hidden sm:inline-flex items-center gap-1.5 font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("legal.common.backToHome", { defaultValue: "Back to home" })}
              </Link>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      <article className="container mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-16 flex-1 font-reading">
        <h1 className="font-ui text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
          {title}
        </h1>
        {lastUpdated && (
          <p className="mt-2 font-ui text-sm text-muted-foreground">
            {t("legal.common.lastUpdated", { defaultValue: "Last updated" })}:{" "}
            {lastUpdated}
          </p>
        )}
        <Separator className="my-8" />
        <div className="space-y-10 text-foreground/90 leading-relaxed">
          {children}
        </div>
      </article>

      <footer className="bg-muted border-t border-border mt-12">
        <div className="container mx-auto px-4 md:px-16 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 font-ui text-xs text-muted-foreground">
            <span>
              {t("landing.footer.copyright", {
                defaultValue: "© 2025 DeutschGenie. All rights reserved.",
              })}
            </span>
            <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link
                to={ROUTES.PRIVACY}
                className="hover:text-foreground transition-colors"
              >
                {t("legal.common.crossLinks.privacy", {
                  defaultValue: "Privacy Policy",
                })}
              </Link>
              <Link
                to={ROUTES.TERMS}
                className="hover:text-foreground transition-colors"
              >
                {t("legal.common.crossLinks.terms", {
                  defaultValue: "Terms of Service",
                })}
              </Link>
              <Link
                to={ROUTES.IMPRINT}
                className="hover:text-foreground transition-colors"
              >
                {t("legal.common.crossLinks.imprint", {
                  defaultValue: "Imprint",
                })}
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </main>
  );
}

interface LegalSectionProps {
  readonly id?: string;
  readonly title: string;
  readonly children: ReactNode;
}

export function LegalSection({ id, title, children }: LegalSectionProps) {
  return (
    <section id={id} className="space-y-3 scroll-mt-24">
      <h2 className="font-ui text-xl sm:text-2xl font-semibold text-primary">
        {title}
      </h2>
      <div className="space-y-3 text-base">{children}</div>
    </section>
  );
}
